import screenDisplayConditionEngine from "./screenDisplayConditionEngine";
import urlDisplayConditionEngine from "./urlDisplayConditionEngine";
import userDataDisplayConditionEngine from "./userDataDisplayConditionEngine";
import userAgentDisplayConditionEngine from "./userAgentDisplayConditionEngine";

/**
 * Loops through user data conditions and returns once all the conditions
 *  - or none of the conditions are met.
 *
 *  And/or conditions are expressed as a 2d array defined in CLoudCMS
 *
 *  @param {Object} userDataRules
 *
 *  @return {Boolean}
 */
const parseUserDataCondition = (userDataRules) => {
  if (!userDataRules) return true;

  const { conditions } = userDataRules;

  //Check if at least one of the conditions are met. These are OR conditions
  return conditions.some((condition) => {
    //Check if all the conditions are met. These are AND conditions
    return condition.every((cond) => {
      return userDataDisplayConditionEngine(cond);
    });
  });
};

/**
 * Loops through screen display conditions and returns once all the conditions
 *  - or none of the conditions are met.
 *
 *  And/or conditions are expressed as a 2d array defined in CLoudCMS
 *
 *  @param {Object} screenDisplayRules - an object with the screen display rules
 *  @param {Object} config - object containing the configs
 *
 *  @return {Boolean}
 */
const parseScreenDisplayCondition = (screenDisplayRules, config) => {
  if (!screenDisplayRules) return true;

  const { conditions } = screenDisplayRules;

  //Check if at least one of the conditions are met. These are OR conditions
  return conditions.some((condition) => {
    //Check if all the conditions are met. These are AND conditions
    return condition.every((cond) => {
      return screenDisplayConditionEngine(cond, config);
    });
  });
};

/**
 * Loops through url conditions and returns once all the conditions
 *  - or none of the conditions are met.
 *
 *  And/or conditions are expressed as a 2d array defineds in the config object
 *
 *  @param {Object} urlRules - an object with the url rules
 *
 *  @return {Boolean}
 */
const parseUrlCondition = (urlRules) => {
  if (!urlRules) return true;

  const { conditions } = urlRules;

  //Check if at least one of the conditions are met. These are OR conditions
  return conditions.some((condition) => {
    //Check if all the conditions are met. These are AND conditions
    return condition.every((cond) => {
      return urlDisplayConditionEngine(cond);
    });
  });
};

/**
 * Loops through user agent conditions and returns once all the conditions
 *  - or none of the conditions are met.
 *
 *  And/or conditions are expressed as a 2d array defined in config
 *
 *  @param {Object} userAgentRules - an object with the user agent rules
 *
 *  @return {Boolean}
 */
const parseUserAgentCondition = (userAgentRules) => {
  if (!userAgentRules) return true;

  const { conditions } = userAgentRules;

  //Check if at least one of the conditions are met. These are OR conditions
  return conditions.some((condition) => {
    //Check if all the conditions are met. These are AND conditions
    return condition.every((cond) => {
      return userAgentDisplayConditionEngine(cond);
    });
  });
};

/**
 * Runs the conditions engine, passes off work to smaller functions
 *
 * @param {Object} conditions - object containing conditions
 * @param {Object} config - object containing the configs to pass to the engine
 *
 * @return {Boolean}
 *
 */
export default (conditions, config = {}) => {
  const options = {
    isClickTrue: config.isClickTrue || false,
    isHoveredTrue: config.isHoveredTrue || false,
    isMouseLeftViewportTrue: config.isMouseLeftViewportTrue || false,
    isIdled: config.idleTimeOut > 0 ? config.idleTimeOut : 0,
    clickCallback: config.clickCallback || (() => {}),
    hoverCallback: config.hoverCallback || (() => {}),
  };

  for (const key in conditions) {
    if (!conditions[key] || !conditions[key].conditions) continue;
    if (
      conditions[key] &&
      conditions[key].conditions &&
      conditions[key].conditions.length === 0
    )
      continue;
    switch (key) {
      case "screenElement":
        if (!parseScreenDisplayCondition(conditions[key], options))
          return false;
        break;

      case "url":
        if (!parseUrlCondition(conditions[key])) return false;
        break;

      case "userData":
        if (!parseUserDataCondition(conditions[key])) return false;
        break;

      case "userAgent":
        if (!parseUserAgentCondition(conditions[key])) return false;
        break;
    }
  }

  return true;
};
